/**
 * Applying — and reverting — a board-agent plan (F-144).
 *
 * The whole reversibility story is here, and it is two mechanisms, not one:
 *
 *  1. ONE UNDO STEP. Every op lands through the session's local-origin
 *     mutators inside one `beginUndoGroup()`, so Ctrl+Z after an apply is the
 *     paste experience: the entire plan comes off at once, chunked rows and
 *     all, and the C6/C8 rules in `core/session.ts` apply unchanged (a row
 *     the plan created that someone has since edited blocks the undo; a cell
 *     someone else wrote after us is skipped).
 *
 *  2. A RECORDED INVERSE. Undo is a stack — you cannot undo the agent's run
 *     from an hour ago without also undoing everything you typed since. So the
 *     applier records, at apply time and from the doc alone, the ops that put
 *     things back: the previous value of every cell it wrote, the ids of every
 *     row and column it created, the contents and position of every row and
 *     column it deleted. `revertAgentRun` applies that inverse later, out of
 *     order, through the SAME machinery — so a revert is itself one undo step
 *     (Ctrl+Z after a revert re-applies the run), and it carries its own
 *     C6/C8 discipline: a cell someone changed since the run is left alone, a
 *     row or column the run created that someone has typed into is kept.
 *
 * What a revert cannot restore: cell formatting on rows/columns the run
 * DELETED (only text is captured), and elements or comments anchored to them.
 * Ctrl+Z restores those exactly, which is why the plan card says so and why
 * destructive ops are never proposed unasked.
 *
 * Pure over the `AgentWriter` slice of `BoardSession` so it is testable
 * against a bare `BoardDoc` — the same reason `undo-group.ts` takes a slice
 * of the UndoManager.
 */
import {
  AGENT_TEMP_COLUMN_PREFIX,
  agentPlanCounts,
  cellKey,
  isAgentTempColumnId,
  type AgentOp,
  type AgentPlan,
  type AgentPlanCounts,
  type BoardDoc,
  type CellScalar,
  type ColumnType,
} from "@kola/shared";

/** The slice of `BoardSession` the applier needs — every write is local-origin. */
export interface AgentWriter {
  readonly board: BoardDoc;
  /** Run `fn` as one local transaction; a no-op (fn never runs) while frozen. */
  batch(fn: () => void): void;
  beginUndoGroup(): () => void;
  insertRows(sheetId: string, count: number, index?: number): string[];
  insertNamedColumn(
    sheetId: string,
    name: string,
    options?: { index?: number | undefined; type?: ColumnType | undefined },
  ): string | null;
  setCellValue(sheetId: string, rowId: string, colId: string, value: CellScalar): void;
  deleteRows(sheetId: string, rowIds: readonly string[]): void;
  deleteColumn(sheetId: string, colId: string): void;
  setColumnName(sheetId: string, colId: string, name: string): boolean;
  /** F-42: a peer is editing this cell right now — the write is skipped. */
  isCellClaimed(sheetId: string, rowId: string, colId: string): boolean;
}

export interface ApplySkips {
  /** Cells left alone because a peer was editing them (F-42). */
  claimed: number;
  /** Cells/rows/columns whose target no longer existed (deleted meanwhile). */
  missing: number;
  /** Revert only: cells someone changed since the run (C8) — left as they are. */
  changed: number;
  /** Revert only: rows the run created that someone has since edited (C6) — kept. */
  blockedRows: number;
  /** Revert only: columns the run created that someone has since edited (C6) — kept. */
  blockedColumns: number;
}

export interface AppliedRun {
  id: string;
  at: number;
  summary: string;
  sheetId: string;
  counts: AgentPlanCounts;
  /** Ops that put the board back, in the order to apply them. */
  inverse: AgentOp[];
  /** The text this run left in each cell it wrote (`cellKey` → text). */
  wrote: Record<string, string>;
  createdRowIds: string[];
  createdColIds: string[];
  skipped: ApplySkips;
}

export type ApplyOutcome =
  | { ok: true; run: AppliedRun }
  | { ok: false; reason: "frozen" | "sheet-missing" };

export type RevertOutcome =
  | { ok: true; skipped: ApplySkips }
  | { ok: false; reason: "frozen" | "sheet-missing" };

export interface ApplyOptions {
  /** Rows per transaction for `insert_rows` (F-90's 500 keeps a 10k import fluid). */
  chunkRows?: number;
  yieldToUi?: () => Promise<void>;
  now?: () => number;
  newId?: () => string;
}

export const APPLY_CHUNK_ROWS = 500;

const defaultYield = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));
const defaultId = (): string => `run${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/** Apply a plan as one undo step and record how to revert it. */
export async function applyAgentPlan(
  writer: AgentWriter,
  plan: AgentPlan,
  options: ApplyOptions = {},
): Promise<ApplyOutcome> {
  if (!writer.board.listSheets().some((s) => s.id === plan.sheetId)) {
    return { ok: false, reason: "sheet-missing" };
  }
  const result = await applyOps(writer, plan.sheetId, plan.ops, {}, options);
  if (!result.ok) return result;
  return {
    ok: true,
    run: {
      id: (options.newId ?? defaultId)(),
      at: (options.now ?? Date.now)(),
      summary: plan.summary,
      sheetId: plan.sheetId,
      counts: agentPlanCounts(plan.ops),
      inverse: result.inverse,
      wrote: result.wrote,
      createdRowIds: result.createdRowIds,
      createdColIds: result.createdColIds,
      skipped: result.skipped,
    },
  };
}

/**
 * Put a run's changes back, leaving alone whatever someone changed since.
 * One undo step of its own — Ctrl+Z after a revert re-applies the run.
 */
export async function revertAgentRun(
  writer: AgentWriter,
  run: AppliedRun,
  options: ApplyOptions = {},
): Promise<RevertOutcome> {
  const { board } = writer;
  if (!board.listSheets().some((s) => s.id === run.sheetId)) {
    return { ok: false, reason: "sheet-missing" };
  }
  const sheetId = run.sheetId;
  const wroteText = (rowId: string, colId: string) => run.wrote[cellKey(rowId, colId)] ?? "";
  const untouchedSinceRun = (rowId: string, colId: string) =>
    board.getCellText(sheetId, rowId, colId) === wroteText(rowId, colId);
  const guard: ApplyGuard = {
    // C8: a cell someone else changed after the run keeps their value. A cell
    // the run never wrote (the contents of a column it deleted, going back
    // into a freshly minted column) has nothing to compare and is restored.
    allowCell: (rowId, colId) =>
      cellKey(rowId, colId) in run.wrote ? untouchedSinceRun(rowId, colId) : true,
    // C6: a row the run created is removed only if nobody typed into it since.
    allowDeleteRow: (rowId) =>
      board.sortedColIds(sheetId).every((colId) => untouchedSinceRun(rowId, colId)),
    allowDeleteColumn: (colId) =>
      board.sortedRowIds(sheetId).every((rowId) => untouchedSinceRun(rowId, colId)),
  };
  const result = await applyOps(writer, sheetId, run.inverse, guard, options);
  if (!result.ok) return result;
  return { ok: true, skipped: result.skipped };
}

/* -------------------------------- core --------------------------------- */

interface ApplyGuard {
  allowCell?: (rowId: string, colId: string) => boolean;
  allowDeleteRow?: (rowId: string) => boolean;
  allowDeleteColumn?: (colId: string) => boolean;
}

interface ApplyResult {
  ok: true;
  inverse: AgentOp[];
  wrote: Record<string, string>;
  createdRowIds: string[];
  createdColIds: string[];
  skipped: ApplySkips;
}

async function applyOps(
  writer: AgentWriter,
  sheetId: string,
  ops: readonly AgentOp[],
  guard: ApplyGuard,
  options: ApplyOptions,
): Promise<ApplyResult | { ok: false; reason: "frozen" }> {
  const { board } = writer;
  const chunkRows = options.chunkRows ?? APPLY_CHUNK_ROWS;
  const yieldToUi = options.yieldToUi ?? defaultYield;
  /** Temp column ids (`new:…`) → the real ids this apply minted. */
  const temp = new Map<string, string>();
  const resolveCol = (ref: string): string | undefined => {
    const id = isAgentTempColumnId(ref) ? temp.get(ref) : ref;
    return id !== undefined && board.hasColumn(sheetId, id) ? id : undefined;
  };
  /** Per-op undo steps, in op order; reversed as a whole at the end. */
  const undoGroups: AgentOp[][] = [];
  const wrote: Record<string, string> = {};
  const createdRowIds: string[] = [];
  const createdColIds: string[] = [];
  const skipped: ApplySkips = { claimed: 0, missing: 0, changed: 0, blockedRows: 0, blockedColumns: 0 };
  let frozen = false;
  /** A transaction that reports whether it ran (false = offline-frozen, F-45). */
  const tx = (fn: () => void): boolean => {
    let landed = false;
    writer.batch(() => {
      fn();
      landed = true;
    });
    if (!landed) frozen = true;
    return landed;
  };

  const endGroup = writer.beginUndoGroup();
  try {
    for (const op of ops) {
      if (frozen) break;
      switch (op.op) {
        case "add_column": {
          let real: string | null = null;
          tx(() => {
            real = writer.insertNamedColumn(sheetId, op.name, { index: op.index, type: op.type });
          });
          if (real === null) {
            frozen = true;
            break;
          }
          temp.set(op.colId, real);
          createdColIds.push(real);
          undoGroups.push([{ op: "delete_column", colId: real }]);
          break;
        }
        case "rename_column": {
          const colId = resolveCol(op.colId);
          if (colId === undefined) {
            skipped.missing++;
            break;
          }
          const prev = board.getColumn(sheetId, colId)?.name ?? "";
          let renamed = false;
          tx(() => {
            renamed = writer.setColumnName(sheetId, colId, op.name);
          });
          if (renamed) undoGroups.push([{ op: "rename_column", colId, name: prev }]);
          break;
        }
        case "delete_column": {
          const colId = resolveCol(op.colId);
          if (colId === undefined) {
            skipped.missing++;
            break;
          }
          if (guard.allowDeleteColumn !== undefined && !guard.allowDeleteColumn(colId)) {
            skipped.blockedColumns++;
            break;
          }
          const info = board.getColumn(sheetId, colId);
          const index = board.sortedColIds(sheetId).indexOf(colId);
          const restoreId = `${AGENT_TEMP_COLUMN_PREFIX}restore-${colId}`;
          const cells = board
            .sortedRowIds(sheetId)
            .map((rowId) => ({ rowId, colId: restoreId, value: board.getCellText(sheetId, rowId, colId) }))
            .filter((cell) => cell.value !== "");
          tx(() => writer.deleteColumn(sheetId, colId));
          undoGroups.push([
            {
              op: "add_column",
              colId: restoreId,
              name: info?.name ?? "",
              ...(info?.type === undefined ? {} : { type: info.type }),
              ...(index < 0 ? {} : { index }),
            },
            ...(cells.length === 0 ? [] : [{ op: "set_cells", cells } satisfies AgentOp]),
          ]);
          break;
        }
        case "set_cells": {
          const writes: { rowId: string; colId: string; value: string; prev: string }[] = [];
          for (const cell of op.cells) {
            const colId = resolveCol(cell.colId);
            if (colId === undefined || !board.hasRow(sheetId, cell.rowId)) {
              skipped.missing++;
              continue;
            }
            if (writer.isCellClaimed(sheetId, cell.rowId, colId)) {
              skipped.claimed++;
              continue;
            }
            if (guard.allowCell !== undefined && !guard.allowCell(cell.rowId, colId)) {
              skipped.changed++;
              continue;
            }
            writes.push({ rowId: cell.rowId, colId, value: cell.value, prev: board.getCellText(sheetId, cell.rowId, colId) });
          }
          if (writes.length === 0) break;
          const landed = tx(() => {
            for (const w of writes) writer.setCellValue(sheetId, w.rowId, w.colId, w.value);
          });
          if (!landed) break;
          for (const w of writes) wrote[cellKey(w.rowId, w.colId)] = w.value;
          undoGroups.push([
            { op: "set_cells", cells: writes.map((w) => ({ rowId: w.rowId, colId: w.colId, value: w.prev })) },
          ]);
          break;
        }
        case "insert_rows": {
          const ids: string[] = [];
          for (let start = 0; start < op.rows.length && !frozen; start += chunkRows) {
            const chunk = op.rows.slice(start, start + chunkRows);
            let chunkIds: string[] = [];
            tx(() => {
              chunkIds = writer.insertRows(
                sheetId,
                chunk.length,
                op.index === undefined ? undefined : op.index + start,
              );
              chunkIds.forEach((rowId, i) => {
                for (const [ref, value] of Object.entries(chunk[i]?.cells ?? {})) {
                  const colId = resolveCol(ref);
                  if (colId === undefined) {
                    skipped.missing++;
                    continue;
                  }
                  writer.setCellValue(sheetId, rowId, colId, value);
                  wrote[cellKey(rowId, colId)] = value;
                }
              });
            });
            if (chunkIds.length === 0) {
              frozen = true;
              break;
            }
            ids.push(...chunkIds);
            if (start + chunkRows < op.rows.length) await yieldToUi();
          }
          if (ids.length > 0) {
            createdRowIds.push(...ids);
            undoGroups.push([{ op: "delete_rows", rowIds: ids }]);
          }
          break;
        }
        case "delete_rows": {
          const sorted = board.sortedRowIds(sheetId);
          const colIds = board.sortedColIds(sheetId);
          const captured: { rowId: string; index: number; cells: Record<string, string> }[] = [];
          for (const rowId of op.rowIds) {
            const index = sorted.indexOf(rowId);
            if (index === -1) {
              skipped.missing++;
              continue;
            }
            if (guard.allowDeleteRow !== undefined && !guard.allowDeleteRow(rowId)) {
              skipped.blockedRows++;
              continue;
            }
            const cells: Record<string, string> = {};
            for (const colId of colIds) {
              const text = board.getCellText(sheetId, rowId, colId);
              if (text !== "") cells[colId] = text;
            }
            captured.push({ rowId, index, cells });
          }
          if (captured.length === 0) break;
          const landed = tx(() => writer.deleteRows(sheetId, captured.map((c) => c.rowId)));
          if (!landed) break;
          // Restore each contiguous run of rows at its original position; the
          // inverse runs later ops' inverses first, so the indexes line up.
          captured.sort((a, b) => a.index - b.index);
          const groups: { index: number; rows: { cells: Record<string, string> }[] }[] = [];
          for (const row of captured) {
            const last = groups[groups.length - 1];
            if (last !== undefined && last.index + last.rows.length === row.index) last.rows.push({ cells: row.cells });
            else groups.push({ index: row.index, rows: [{ cells: row.cells }] });
          }
          undoGroups.push(groups.map((g) => ({ op: "insert_rows", index: g.index, rows: g.rows })));
          break;
        }
      }
    }
  } finally {
    endGroup();
  }
  if (frozen) return { ok: false, reason: "frozen" };
  return {
    ok: true,
    inverse: undoGroups.reverse().flat(),
    wrote,
    createdRowIds,
    createdColIds,
    skipped,
  };
}
